import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import {
  EMPTY_FILTER_VALUES,
  FilterBarConfig,
  FilterBarValues,
  FilterCategoryOption,
  FilterCityOption,
  FilterVerticalOption,
} from './filter-bar.types';

@Component({
  selector: 'op-filter-bar',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
  ],
  templateUrl: './filter-bar.component.html',
  styleUrl: './filter-bar.component.scss',
})
export class FilterBarComponent implements OnChanges {
  private readonly fb = inject(FormBuilder);

  @Input() searchPlaceholder = 'Search...';
  @Input() config: FilterBarConfig = {};
  @Input() initialValues?: Partial<FilterBarValues>;

  @Input() set categoryOptions(val: FilterCategoryOption[]) { this._categoryOptions.set(val); }
  @Input() set cityOptions(val: FilterCityOption[]) { this._cityOptions.set(val); }
  @Input() set verticalOptions(val: FilterVerticalOption[]) { this._verticalOptions.set(val); }

  @Output() readonly filterChange = new EventEmitter<FilterBarValues>();

  readonly _categoryOptions = signal<FilterCategoryOption[]>([]);
  readonly _cityOptions = signal<FilterCityOption[]>([]);
  readonly _verticalOptions = signal<FilterVerticalOption[]>([]);

  readonly categoriesByVertical = computed(() => {
    const groups: Record<string, { label: string; options: FilterCategoryOption[] }> = {};
    for (const cat of this._categoryOptions()) {
      const key = cat.vertical || 'other';
      if (!groups[key]) groups[key] = { label: this.verticalLabel(key), options: [] };
      groups[key].options.push(cat);
    }
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, g]) => g);
  });

  readonly hasGroups = computed(() => this._categoryOptions().some(c => !!c.vertical));

  readonly filterForm = this.fb.nonNullable.group({
    search:            [''],
    categoryIds:       [[] as string[]],
    cityCodes:         [[] as string[]],
    cityAvailability:  [[] as string[]],
    vertical:          [''],
    trustStatus:       [''],
    insuranceVerified: [''],
    isActive:          [''],
  });

  private readonly _hasApplied = signal(false);
  readonly hasApplied = this._hasApplied.asReadonly();

  readonly TRUST_OPTIONS = [
    { value: 'trusted',   label: 'Trusted'   },
    { value: 'caution',   label: 'Caution'   },
    { value: 'pre_block', label: 'Pre-Block' },
    { value: 'blocked',   label: 'Blocked'   },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialValues'] && this.initialValues) {
      const vals = { ...EMPTY_FILTER_VALUES, ...this.initialValues };
      this.filterForm.patchValue(vals, { emitEvent: false });
      this._hasApplied.set(this.checkHasApplied(vals));
    }
  }

  apply(): void {
    const values = this.filterForm.getRawValue();
    this._hasApplied.set(this.checkHasApplied(values));
    this.filterChange.emit(values);
  }

  reset(): void {
    this.filterForm.reset(EMPTY_FILTER_VALUES);
    this._hasApplied.set(false);
    this.filterChange.emit({ ...EMPTY_FILTER_VALUES });
  }

  clearControl(name: string): void {
    const ctrl = this.filterForm.get(name);
    if (!ctrl) return;
    ctrl.setValue(Array.isArray(ctrl.value) ? [] : '');
    this.apply();
  }

  private checkHasApplied(f: FilterBarValues): boolean {
    return (
      f.search !== '' ||
      f.categoryIds.length > 0 ||
      f.cityCodes.length > 0 ||
      f.cityAvailability.length > 0 ||
      f.vertical !== '' ||
      f.trustStatus !== '' ||
      f.insuranceVerified !== '' ||
      f.isActive !== ''
    );
  }

  private verticalLabel(key: string): string {
    return key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }
}